// The item registry — single source of truth, mirroring deco/index.ts. Items are
// CONTENT: authored as JSON (content/items/<type>.json + a model doc in
// content/models/), validated + hydrated by src/content/load.ts. Add an item =
// two JSON files + `bun run gen` (the ItemType union regenerates from content) —
// no code edits. See content/SCHEMA.md.

import * as THREE from "three";
import type { ItemType, Gear } from "../shared.ts";
import { buildItem, isWeapon, type ItemSpec, type ShieldSpec } from "./define.ts";
import type { WeaponArchetypeId } from "../anim/archetypes.ts";
import { loadedItems } from "../content/load.ts";
import { renameItemType } from "./aliases.ts";

export { renameItemType, ITEM_TYPE_RENAMES } from "./aliases.ts";
export const ITEMS: ItemSpec[] = loadedItems;

/** Every weapon item that shares an animation archetype (studio pickers +
 *  "used by" notes) — the ONE "which items use this archetype" query. */
export function weaponsOfArchetype(id: WeaponArchetypeId): ItemSpec[] {
  return ITEMS.filter((s) => isWeapon(s) && s.weapon.archetype === id);
}

const byType = new Map<ItemType, ItemSpec>(ITEMS.map((s) => [s.type, s]));

/**
 * Map a raw type string (wire / DB / renamed id) onto a live ItemType, or null
 * if it is unknown even after aliases. Prefer this at persistence boundaries
 * so live state never carries retired ids.
 */
export function resolveItemType(raw: string): ItemType | null {
  const t = renameItemType(raw);
  return byType.has(t as ItemType) ? (t as ItemType) : null;
}

/** The spec for an item type (throws on an unknown type — keeps the union honest).
 *  Applies ITEM_TYPE_RENAMES so a stale "lance" still resolves to spear. */
export function itemSpec(type: ItemType | string): ItemSpec {
  const key = renameItemType(type) as ItemType;
  const s = byType.get(key);
  if (!s) throw new Error(`unknown item type: ${type}`);
  return s;
}

/** Tolerant lookup for values that may OUTLIVE the content set (persisted gear,
 *  wire input): undefined instead of a throw on an unknown type. Applies renames. */
export function itemSpecOrNull(type: string): ItemSpec | undefined {
  return byType.get(renameItemType(type) as ItemType);
}

/** The weapon stats for an equipped item type, or null if it isn't a weapon.
 *  Co-located with armorPct/blockSoak — all item-spec-derived combat helpers,
 *  shared by the server handlers + the studio arena so the rules can't drift. */
export function weaponOf(type: ItemType) {
  const s = itemSpec(type);
  return isWeapon(s) ? s.weapon : null;
}

/** A fresh Group for an item type, feet at y=0, ready to position in the world. */
export function buildItemMesh(type: ItemType): THREE.Group {
  return buildItem(itemSpec(type));
}

/** Total % damage reduction from worn gear + optional skill-box armor bonus
 *  (capped — a full plate set softens hits, it doesn't make you unhittable).
 *  Shared by the server's damagePlayer and the studio's combat simulator so the
 *  soak rule can't drift. */
const ARMOR_CAP = 70;
export function armorPct(gear: Gear, boxArmor = 0): number {
  let sum = boxArmor;
  for (const t of Object.values(gear)) {
    if (t) sum += itemSpecOrNull(t)?.wear?.armor ?? 0;
  }
  return Math.min(ARMOR_CAP, sum);
}

/** A raised shield eats a FRONTAL hit (plan 14): block% off the damage,
 *  staminaPerHit from the pool; an emptied pool breaks the block (the caller
 *  staggers the blocker). THE one copy of the soak economy — the server's
 *  damagePlayer and the studio arena share it, same discipline as armorPct
 *  above. The frontal-arc test itself is the sim's inFrontalArc. */
export function blockSoak(
  sh: ShieldSpec,
  dmg: number,
  stamina: number,
): { dmg: number; stamina: number; broke: boolean } {
  const drained = stamina - sh.staminaPerHit;
  return {
    dmg: dmg * (1 - sh.block / 100),
    stamina: Math.max(0, drained),
    broke: drained <= 0,
  };
}
